import Card from "@/components/Card";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import Modal from "@/components/Modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { getMasterSystem } from "@/services/getData";
import { NilaiRapor as NilaiRaporType } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { FaNoteSticky } from "react-icons/fa6";
import { Link, useSearchParams } from "react-router-dom";

const menus = [
  { number: 1, name: "Kelas 10 (Semester 1)", kelas: 10, semester: 1 },
  { number: 2, name: "Kelas 10 (Semester 2)", kelas: 10, semester: 2 },
  { number: 3, name: "Kelas 10 (Semester 3)", kelas: 10, semester: 3 },
  { number: 4, name: "Kelas 11 (Semester 1)", kelas: 11, semester: 1 },
  { number: 5, name: "Kelas 11 (Semester 2)", kelas: 11, semester: 2 },
  { number: 6, name: "Kelas 12 (Semester 1)", kelas: 12, semester: 1 },
];

type ValidasiState = {
  id_nilai: string;
  validasi_ver1: number | null;
  validasi_ver2: number | null;
  validasi_ver3: number | null;
  ket_ver1: string;
  ket_ver2: string;
  ket_ver3: string;
  nilai_ver1: string | number;
  nilai_ver2: string | number;
  nilai_ver3: string | number;

  verifikatorKe: string | number;
};

type InvalidBerkasRaporState = {
  id_data_pendaftaran: string;
  id_data_berkas_siswa: string;
  is_valid: boolean;
  catatan_verifikator: string;
};

const getDataNilaiRapor = async (nomor_pendaftaran: string) => {
  const response = await api.get(
    `/verifikator/peserta-ijazah/show/nilai-rapor/${nomor_pendaftaran}`,
  );
  return response.data?.data;
};

const patchDataValidasi = async (form: ValidasiState) => {
  const response = await api.patch(
    "/verifikator/peserta-ijazah/validate-nilai-rapor",
    form,
  );
  return response.data;
};

const patchDataInvalidBerkasRapor = async (form: InvalidBerkasRaporState) => {
  const response = await api.patch(
    "/verifikator/peserta-ijazah/validate-invalid-berkas-rapor",
    form,
  );
  return response.data;
};

export default function VerifikasiRapor() {
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const nomorPendaftaranParams = searchParams.get("no") || "";
  const raporParams = searchParams.get("rapor");

  const [activeMenu, setActiveMenu] = useState(
    raporParams ? Number(raporParams) : 1,
  );

  const [showConfirmInvalidBerkas, setShowConfirmInvalidBerkas] =
    useState(false);
  const [selectedNilaiRapor, setSelectedNilaiRapor] =
    useState<NilaiRaporType | null>(null);
  const [showCatatan, setShowCatatan] = useState(false);
  const [form, setForm] = useState({
    catatan: "",
    nilai_perbaikan: "",
  });

  const [formInvalidRapor, setFormInvalidRapor] = useState({
    id_data_pendaftaran: "",
    id_data_berkas_siswa: "",
    is_valid: false,
    catatan_verifikator: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["dataNilaiRapor", nomorPendaftaranParams],
    queryFn: () => getDataNilaiRapor(nomorPendaftaranParams || ""),
    enabled: !!nomorPendaftaranParams,
  });

  const menu = menus.find((menu) => menu.number === activeMenu);
  const listNilaiRapor = menu
    ? (data?.dataNilaiRapor?.[menu.kelas]?.[menu.semester] ?? [])
    : [];

  const isVerifikatorKe3 =
    user?.jenis_user?.includes("verifikator_raport_all") ||
    data?.verifikatorKe == 3;

  const queryClient = useQueryClient();
  const {
    mutate,
    isPending: isPendingValidation,
    error: errorValidation,
  } = useMutation({
    mutationFn: patchDataValidasi,
    onSuccess: (data) => {
      setForm((prev) => ({ ...prev, catatan: "", nilai_perbaikan: "" }));
      queryClient.invalidateQueries({
        queryKey: ["dataNilaiRapor"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket"],
      });
      toast.success(data?.message || "Success.");
      setShowCatatan(false);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const {
    mutate: mutateInvalidBerkasRapor,
    isPending: isPendingInvalidBerkasRapor,
    error: errorInvalidBerkasRapor,
  } = useMutation({
    mutationFn: patchDataInvalidBerkasRapor,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataNilaiRapor"],
      });
      setShowConfirmInvalidBerkas(false);
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleValidasi = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const verifikatorKe = data?.verifikatorKe;

    const payload: any = {
      id_nilai: selectedNilaiRapor?.id_nilai || "",
      verifikatorKe,
    };

    if (verifikatorKe == 1) {
      payload.validasi_ver1 = selectedNilaiRapor?.validasi_ver1 || 0;
      payload.ket_ver1 = form.catatan;
      payload.nilai_ver1 = form.nilai_perbaikan;
    } else if (verifikatorKe == 2) {
      payload.validasi_ver2 = selectedNilaiRapor?.validasi_ver2 || 0;
      payload.ket_ver2 = form.catatan;
      payload.nilai_ver2 = form.nilai_perbaikan;
    } else if (verifikatorKe == 3) {
      payload.validasi_ver3 = selectedNilaiRapor?.validasi_ver3 || 0;
      payload.ket_ver3 = form.catatan;
      payload.nilai_ver3 = form.nilai_perbaikan;
    }

    mutate(payload);
  };

  const handleInvalidBerkasRapor = () => {
    mutateInvalidBerkasRapor({
      ...formInvalidRapor,
      id_data_pendaftaran: String(data?.dataPendaftaran?.id_data_pendaftaran),
      id_data_berkas_siswa: String(data?.dataBerkasRapor?.id_data_berkas_siswa),
      is_valid: false,
    });
  };

  const { data: system } = useQuery({
    queryKey: ["masterSystem"],
    queryFn: getMasterSystem,
  });

  const isWaktuSanggahBerkas =
    system && system[0]?.is_waktu_sanggah_berkas_daftarkembali;

  const handleValidBerkasRapor = () => {
    mutateInvalidBerkasRapor({
      ...formInvalidRapor,
      id_data_pendaftaran: String(data?.dataPendaftaran?.id_data_pendaftaran),
      id_data_berkas_siswa: String(data?.dataBerkasRapor?.id_data_berkas_siswa),
      is_valid: true,
    });
  };

  const returnUrl = searchParams.get("returnTo")
    ? decodeURIComponent(searchParams.get("returnTo")!)
    : "/verifikator/raport/verifikasi-raport-peserta";

  if (isLoading) {
    return (
      <div className="flex flex-col h-full min-h-screen bg-background dark:bg-[#09090b] p-4 md:p-6">
        {/* HEADER SKELETON */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            {/* Back Button Skeleton */}
            <div className="h-10 w-10 rounded-lg bg-muted dark:bg-zinc-800 animate-pulse" />
            <div className="space-y-2">
              {/* Title Skeleton */}
              <div className="h-7 w-48 sm:w-64 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
              {/* Subtitle Skeleton */}
              <div className="h-4 w-64 sm:w-96 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
            </div>
          </div>
          {/* Badge Skeleton */}
          <div className="h-8 w-32 bg-muted dark:bg-zinc-800 rounded-full animate-pulse" />
        </div>

        {/* MAIN CONTENT SKELETON */}
        <section className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-6 xl:h-[calc(100vh-160px)]">
          {/* KOLOM KIRI SKELETON (Dokumen) */}
          <div className="flex flex-col h-[70vh] xl:h-full border border-border dark:border-zinc-800 rounded-xl overflow-hidden bg-card shadow-sm">
            <div className="p-4 border-b border-border dark:border-zinc-800 flex justify-between items-center gap-4">
              <div className="h-6 w-1/3 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
              <div className="h-8 w-32 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
            </div>
            {/* Iframe Placeholder */}
            <div className="flex-1 bg-muted/30 dark:bg-zinc-900/50 animate-pulse flex items-center justify-center">
              <div className="h-16 w-16 text-muted-foreground/30 animate-pulse">
                {/* Ikon opsional untuk memberi kesan loading dokumen */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
            </div>
          </div>

          {/* KOLOM KANAN SKELETON (Tabel Nilai) */}
          <div className="flex flex-col h-[80vh] xl:h-full border border-border dark:border-zinc-800 rounded-xl overflow-hidden bg-card shadow-sm">
            <div className="p-4 sm:p-5 border-b border-border dark:border-zinc-800">
              {/* Nama Siswa Skeleton */}
              <div className="h-6 w-3/4 bg-muted dark:bg-zinc-800 rounded-md animate-pulse mb-5" />
              {/* Tabs Skeleton */}
              <div className="flex gap-2">
                <div className="h-9 w-20 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
                <div className="h-9 w-24 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
                <div className="h-9 w-24 bg-muted dark:bg-zinc-800 rounded-md animate-pulse" />
              </div>
            </div>

            {/* Area Tabel Skeleton */}
            <div className="p-0 overflow-hidden">
              <div className="w-full">
                {/* Header Tabel Skeleton */}
                <div className="flex px-4 py-3 border-b border-border dark:border-zinc-800 bg-muted/20 dark:bg-zinc-900/30 gap-4">
                  <div className="h-5 w-1/3 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
                  <div className="h-5 w-16 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
                  <div className="h-5 w-24 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
                  <div className="h-5 w-1/4 bg-muted dark:bg-zinc-800 rounded animate-pulse" />
                </div>

                {/* Baris Tabel Skeleton (Generate 7 baris) */}
                {Array(7)
                  .fill(null)
                  .map((_, index) => (
                    <div
                      key={index}
                      className="flex px-4 py-4 border-b border-border dark:border-zinc-800 gap-4 items-center"
                    >
                      <div className="h-5 w-1/3 bg-muted/80 dark:bg-zinc-800/80 rounded animate-pulse" />
                      <div className="h-5 w-10 mx-3 bg-muted/80 dark:bg-zinc-800/80 rounded animate-pulse" />
                      <div className="h-9 w-32 bg-muted/80 dark:bg-zinc-800/80 rounded-md animate-pulse" />
                      <div className="h-5 w-1/4 bg-muted/80 dark:bg-zinc-800/80 rounded animate-pulse" />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full min-h-screen bg-background dark:bg-[#09090b] text-foreground transition-colors duration-200 p-4 md:p-6">
      {/* HEADER PAGE & TOMBOL KEMBALI */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link
            to={returnUrl}
            className="inline-flex items-center justify-center rounded-lg border border-border bg-card dark:border-zinc-800 dark:bg-zinc-900 hover:bg-muted dark:hover:bg-zinc-800 text-muted-foreground hover:text-foreground h-10 w-10 transition-colors shadow-sm"
            title="Kembali"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Verifikasi Rapor
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Tinjau dan sesuaikan nilai pendaftar dengan dokumen fisik.
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className="w-fit bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary-foreground px-4 py-1.5 text-sm font-semibold shadow-sm"
        >
          Verifikator ke-{data?.verifikatorKe}
        </Badge>
      </div>

      <section className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-6 xl:h-[calc(100vh-160px)]">
        {/* KOLOM KIRI: DOKUMEN RAPOR */}
        <div className="flex flex-col h-[70vh] xl:h-full border border-border dark:border-zinc-800 rounded-xl overflow-hidden bg-card text-card-foreground shadow-sm relative">
          <div className="z-10 p-4 border-b border-border dark:border-zinc-800 bg-muted/30 dark:bg-zinc-900/50 backdrop-blur-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="font-bold text-lg line-clamp-2">
              {data?.dataBerkasRapor?.sys_berkas?.jenis_berkas
                ?.nama_jenis_berkas || "Berkas Rapor"}
            </h2>
            {data?.dataBerkasRapor?.is_valid === 0 ? (
              <div>
                <div className="bg-destructive/10 text-destructive dark:text-red-400 px-3 py-2 rounded-lg text-sm border border-destructive/20 max-w-sm">
                  <span className="font-semibold">Berkas tidak valid.</span>{" "}
                  <span className="opacity-80">
                    (Ket: {data?.dataBerkasRapor?.catatan_verifikator})
                  </span>
                </div>
                {isWaktuSanggahBerkas && (
                  <Button
                    variant="default"
                    size="sm"
                    className="whitespace-nowrap shadow-sm"
                    onClick={() => handleValidBerkasRapor()}
                  >
                    Tandai Berkas Valid
                  </Button>
                )}
              </div>
            ) : (
              <Button
                variant="destructive"
                size="sm"
                className="whitespace-nowrap shadow-sm"
                onClick={() => setShowConfirmInvalidBerkas(true)}
              >
                Tandai Berkas Tidak Valid
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-4 h-[200vh] p-4 bg-zinc-100 dark:bg-zinc-900">
            {/* Berkas Sanggah */}
            <div className="flex-1 flex flex-col gap-2">
              <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                Berkas Sanggah
              </span>
              <div className="relative flex-1 border border-zinc-300 dark:border-zinc-700 rounded-lg overflow-hidden">
                <iframe
                  src={data?.dataBerkasRapor?.path_sanggah_berkas}
                  title="Raport Sanggah"
                  className="absolute inset-0 h-full w-full border-0"
                />
              </div>
            </div>
            {/* Berkas Rapor Asli */}
            <div className="flex-1 flex flex-col gap-2">
              <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                Berkas Rapor Asli
              </span>
              <div className="relative flex-1 border border-zinc-300 dark:border-zinc-700 rounded-lg overflow-hidden">
                <iframe
                  src={data?.dataBerkasRapor?.path_berkas}
                  title="Raport Asli"
                  className="absolute inset-0 h-full w-full border-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* KOLOM KANAN: TABEL NILAI */}
        <Card className="flex flex-col h-[80vh] xl:h-full overflow-hidden shadow-sm border-border dark:border-zinc-800 bg-card">
          <div className="p-4 sm:p-5 border-b border-border dark:border-zinc-800">
            <h2 className="font-bold text-lg flex flex-wrap items-center gap-2">
              <span>Nilai Siswa</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-primary">
                {data?.dataPendaftaran?.data_siswa?.nama_lengkap}
              </span>
              <span className="text-muted-foreground hidden sm:inline">/</span>
              <span className="text-muted-foreground text-sm font-medium w-full sm:w-auto mt-1 sm:mt-0">
                {data?.dataPendaftaran?.data_siswa?.sekolah?.nama_sekolah}
              </span>
            </h2>

            {/* TABS MENU */}
            <div className="mt-5 inline-flex p-1 bg-muted/50 dark:bg-zinc-900 border border-border dark:border-zinc-800 rounded-lg overflow-x-auto max-w-full no-scrollbar">
              {menus.map((menu) => (
                <button
                  type="button"
                  key={menu.number}
                  className={`whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                    activeMenu === menu.number
                      ? "bg-background dark:bg-zinc-800 text-primary shadow-sm ring-1 ring-border dark:ring-zinc-700"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted dark:hover:bg-zinc-800/50"
                  }`}
                  onClick={() => setActiveMenu(menu.number)}
                >
                  {menu.name}
                </button>
              ))}
            </div>
          </div>

          {/* TABEL */}
          <div className="flex-1 overflow-auto relative">
            <table className="w-full text-sm text-left border-collapse">
              <thead className="sticky top-0 z-10 bg-muted/95 dark:bg-zinc-900/95 backdrop-blur-sm text-muted-foreground shadow-sm">
                <tr>
                  <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800">
                    Mata Pelajaran
                  </th>
                  <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 text-center">
                    Nilai Pength.
                  </th>
                  <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 min-w-[140px]">
                    Status
                  </th>
                  {isVerifikatorKe3 ? (
                    <>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Nilai Ver 1
                      </th>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Status Ver 1
                      </th>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Nilai Ver 2
                      </th>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Status Ver 2
                      </th>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Selisih (V1-V2)
                      </th>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Selisih (Nilai Asli - V1)
                      </th>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Selisih (Nilai Asli - V2)
                      </th>
                      <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800 whitespace-nowrap">
                        Nilai Ver 3
                      </th>
                    </>
                  ) : (
                    <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800">
                      Nilai Validasi
                    </th>
                  )}
                  <th className="py-3 px-4 font-semibold border-b border-border dark:border-zinc-800">
                    Catatan Validasi
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border dark:divide-zinc-800">
                {listNilaiRapor.length > 0 ? (
                  listNilaiRapor.map((item: any) => {
                    const currentItem =
                      selectedNilaiRapor?.id_nilai === item.id_nilai
                        ? selectedNilaiRapor
                        : item;
                    const verKe = data?.verifikatorKe;
                    const keyValidasi =
                      verKe === 1
                        ? "validasi_ver1"
                        : verKe === 2
                          ? "validasi_ver2"
                          : "validasi_ver3";
                    const keyNilai =
                      verKe === 1
                        ? "nilai_ver1"
                        : verKe === 2
                          ? "nilai_ver2"
                          : "nilai_ver3";
                    const keyKeterangan =
                      verKe === 1
                        ? "ket_ver1"
                        : verKe === 2
                          ? "ket_ver2"
                          : "ket_ver3";
                    // const isGapNilai = item["nilai_ver1"] != item["nilai_ver2"];
                    const merah =
                      item["nilai_skala_100"] > item["nilai_ver1"] ||
                      item["nilai_skala_100"] > item["nilai_ver2"];

                    // Penentuan warna baris (Support Dark Mode)
                    let rowColorClass =
                      "hover:bg-muted/30 dark:hover:bg-zinc-800/30 transition-colors";
                    if (
                      !isVerifikatorKe3 &&
                      (item[keyValidasi] == 0 || item[keyValidasi] == null)
                    ) {
                      rowColorClass += " bg-amber-500/5 dark:bg-amber-500/10";
                    } else if (isVerifikatorKe3 && merah) {
                      rowColorClass +=
                        item["nilai_ver3"] !== null
                          ? " bg-emerald-500/10 dark:bg-emerald-500/10"
                          : " bg-destructive/10 dark:bg-destructive/10";
                    }

                    return (
                      <tr key={item.id_nilai} className={rowColorClass}>
                        <td className="py-3 px-4 font-medium text-foreground">
                          {item.nama_mata_pelajaran}
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-foreground">
                          {item.nilai_skala_100}
                        </td>
                        <td className="py-3 px-4">
                          <Select
                            value={
                              currentItem[keyValidasi] === 2
                                ? "Tidak Valid"
                                : currentItem[keyValidasi] === 1
                                  ? "Valid"
                                  : currentItem[keyValidasi] === 0
                                    ? "Belum Dicek"
                                    : ""
                            }
                            onValueChange={(value) => {
                              const status =
                                value === "Tidak Valid"
                                  ? 2
                                  : value === "Valid"
                                    ? 1
                                    : 0;
                              setSelectedNilaiRapor({
                                ...item,
                                validasi_ver1: status,
                                validasi_ver2: status,
                                validasi_ver3: status,
                              });

                              const verifikatorKe = data?.verifikatorKe;
                              if (status === 2) setShowCatatan(true);
                              else {
                                const payload = {
                                  id_nilai: item.id_nilai || "",
                                  validasi_ver1:
                                    verifikatorKe == 1 ? status : null,
                                  validasi_ver2:
                                    verifikatorKe == 2 ? status : null,
                                  validasi_ver3:
                                    verifikatorKe == 3 ? status : null,
                                  ket_ver1: "",
                                  ket_ver2: "",
                                  ket_ver3: "",
                                  nilai_ver1:
                                    verifikatorKe == 1
                                      ? item.nilai_skala_100
                                      : "",
                                  nilai_ver2:
                                    verifikatorKe == 2
                                      ? item.nilai_skala_100
                                      : "",
                                  nilai_ver3:
                                    verifikatorKe == 3
                                      ? item.nilai_skala_100
                                      : "",
                                  verifikatorKe: verifikatorKe,
                                };
                                mutate(payload);
                              }
                            }}
                          >
                            <SelectTrigger className="w-full min-w-[130px] bg-background dark:bg-zinc-900 border-border dark:border-zinc-700 focus:ring-primary/50 shadow-sm">
                              <SelectValue placeholder="Pilih status" />
                            </SelectTrigger>
                            <SelectContent
                              side="top"
                              className="dark:bg-zinc-900 dark:border-zinc-800"
                            >
                              {["Belum Dicek", "Valid", "Tidak Valid"].map(
                                (status) => (
                                  <SelectItem
                                    key={status}
                                    value={status}
                                    className="dark:focus:bg-zinc-800"
                                  >
                                    {status}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        </td>
                        {isVerifikatorKe3 ? (
                          <>
                            <td className="py-3 px-4">
                              <span className="font-semibold block">
                                {item["nilai_ver1"]}
                              </span>
                              <span className="text-xs text-muted-foreground truncate max-w-[100px] block">
                                ({item["nama_verifikator1"]})
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <Badge
                                variant="secondary"
                                className="font-normal text-xs whitespace-nowrap dark:bg-zinc-800 dark:text-zinc-300 border-transparent"
                              >
                                {item["validasi_ver1"] == 2
                                  ? "Tidak Valid"
                                  : item["validasi_ver1"] == 1
                                    ? "Valid"
                                    : "Belum Dicek"}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-semibold block">
                                {item["nilai_ver2"]}
                              </span>
                              <span className="text-xs text-muted-foreground truncate max-w-[100px] block">
                                ({item["nama_verifikator2"]})
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <Badge
                                variant="secondary"
                                className="font-normal text-xs whitespace-nowrap dark:bg-zinc-800 dark:text-zinc-300 border-transparent"
                              >
                                {item["validasi_ver2"] == 2
                                  ? "Tidak Valid"
                                  : item["validasi_ver2"] == 1
                                    ? "Valid"
                                    : "Belum Dicek"}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`font-mono font-medium ${Number(item["nilai_ver1"]) - Number(item["nilai_ver2"] ?? 0) !== 0 ? "text-destructive dark:text-red-400" : ""}`}
                              >
                                {/* {Number(item["nilai_skala_100"]) -
                                  Number(item["nilai_ver3"] ?? 0)} */}
                                {Number(item["nilai_ver1"]) -
                                  Number(item["nilai_ver2"] ?? 0)}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`font-mono font-medium ${Number(item["nilai_skala_100"]) - Number(item["nilai_ver1"] ?? 0) !== 0 ? "text-destructive dark:text-red-400" : ""}`}
                              >
                                {Number(item["nilai_skala_100"]) -
                                  Number(item["nilai_ver1"] ?? 0)}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`font-mono font-medium ${Number(item["nilai_skala_100"]) - Number(item["nilai_ver2"] ?? 0) !== 0 ? "text-destructive dark:text-red-400" : ""}`}
                              >
                                {Number(item["nilai_skala_100"]) -
                                  Number(item["nilai_ver2"] ?? 0)}
                              </span>
                            </td>
                            <td className="py-3 px-4 font-semibold">
                              {item["nilai_ver3"]}
                            </td>
                          </>
                        ) : (
                          <td className="py-3 px-4 font-semibold">
                            {item[keyNilai]}
                          </td>
                        )}

                        <td className="py-3 px-4 text-muted-foreground">
                          {isPendingValidation &&
                          selectedNilaiRapor?.id_nilai === item.id_nilai ? (
                            <div className="flex items-center gap-2 text-primary text-xs font-medium">
                              <Loader2 className="w-4 h-4 animate-spin" />{" "}
                              Memproses...
                            </div>
                          ) : (
                            <span
                              className="text-sm line-clamp-2"
                              title={item[keyKeterangan]}
                            >
                              {item[keyKeterangan] || "-"}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={isVerifikatorKe3 ? 11 : 5}
                      className="py-12 text-center text-muted-foreground"
                    >
                      <div className="flex flex-col items-center justify-center gap-3">
                        <span className="text-3xl">📭</span>
                        <p>Nilai rapor tidak ditemukan</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* MODAL CATATAN TIDAK VALID */}
      <Modal show={showCatatan} onClose={() => setShowCatatan(false)}>
        <form onSubmit={handleValidasi} className="space-y-6">
          <FormHeader
            title={"Nilai Tidak Valid"}
            description={`Mata pelajaran ${selectedNilaiRapor?.nama_mata_pelajaran} (Nilai: ${selectedNilaiRapor?.nilai_skala_100}). Masukkan nilai perbaikan dan alasan.`}
            icon={
              <FaNoteSticky className="text-destructive dark:text-red-400" />
            }
          />
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="dark:text-zinc-200">Nilai Perbaikan</Label>
              <Input
                className="dark:bg-zinc-900 dark:border-zinc-700 focus-visible:ring-primary/50"
                value={form.nilai_perbaikan}
                placeholder="Contoh: 88.8"
                onChange={(event) => {
                  let value = event.target.value.replace(",", ".");
                  if (/^\d*\.?\d*$/.test(value)) {
                    setForm((prev) => ({ ...prev, nilai_perbaikan: value }));
                  }
                }}
                onBlur={() => {
                  setForm((prev) => ({
                    ...prev,
                    nilai_perbaikan: prev.nilai_perbaikan || "",
                  }));
                }}
              />
              <InputError
                text={
                  data?.verifikatorKe === 1
                    ? errorValidation?.response?.data.errors?.nilai_ver1?.join(
                        ",",
                      )
                    : data?.verifikatorKe === 2
                      ? errorValidation?.response?.data.errors?.nilai_ver2?.join(
                          ",",
                        )
                      : errorValidation?.response?.data.errors?.nilai_ver3?.join(
                          ",",
                        )
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-zinc-200">Catatan / Alasan</Label>
              <Textarea
                placeholder="Misal: Nilai yang tercantum di rapor fisik adalah 99..."
                className="dark:bg-zinc-900 dark:border-zinc-700 min-h-[100px] resize-none focus-visible:ring-primary/50"
                value={form.catatan}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, catatan: event.target.value }))
                }
              />
              <InputError
                text={
                  data?.verifikatorKe === 1
                    ? errorValidation?.response?.data.errors?.ket_ver1?.join(
                        ",",
                      )
                    : data?.verifikatorKe === 2
                      ? errorValidation?.response?.data.errors?.ket_ver2?.join(
                          ",",
                        )
                      : errorValidation?.response?.data.errors?.ket_ver3?.join(
                          ",",
                        )
                }
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-border dark:border-zinc-800 mt-6">
            <Button
              type="button"
              variant="outline"
              className="dark:hover:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-300"
              onClick={() => {
                setShowCatatan(false);
                setSelectedNilaiRapor(null);
                setForm((prev) => ({ ...prev, catatan: "" }));
              }}
              disabled={isPendingValidation}
            >
              Batal
            </Button>
            <Button disabled={isPendingValidation} className="min-w-[100px]">
              {isPendingValidation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses...
                </>
              ) : (
                "Simpan"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* MODAL BERKAS INVALID */}
      <Modal
        show={showConfirmInvalidBerkas}
        onClose={() => setShowConfirmInvalidBerkas(false)}
      >
        <div className="flex flex-col gap-6">
          <FormHeader
            title={"Tandai Berkas Tidak Valid"}
            description="Berikan keterangan mengapa berkas PDF rapor ini ditolak."
            icon={null}
          />
          <div className="space-y-2">
            <Label className="dark:text-zinc-200">Keterangan Penolakan</Label>
            <Textarea
              placeholder="Ketik catatan di sini..."
              className="dark:bg-zinc-900 dark:border-zinc-700 min-h-[100px] resize-none focus-visible:ring-primary/50"
              value={formInvalidRapor.catatan_verifikator}
              onChange={(event) =>
                setFormInvalidRapor((prev) => ({
                  ...prev,
                  catatan_verifikator: event.target.value,
                }))
              }
            />
            <InputError
              text={errorInvalidBerkasRapor?.response?.data.errors?.catatan_verifikator?.join(
                ",",
              )}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-border dark:border-zinc-800">
            <Button
              variant="outline"
              className="dark:hover:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-300"
              onClick={() => setShowConfirmInvalidBerkas(false)}
              disabled={isPendingInvalidBerkasRapor}
            >
              Batal
            </Button>
            <Button
              variant="destructive"
              className="min-w-[120px]"
              onClick={() => handleInvalidBerkasRapor()}
              disabled={isPendingInvalidBerkasRapor}
            >
              {isPendingInvalidBerkasRapor ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses...
                </>
              ) : (
                "Tolak Berkas"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
